import { motion } from "framer-motion";
import { Mail, Phone, UserCheck, Cpu, BadgeCheck, TrendingDown,
  Check, FileLock2, CloudUpload, Zap, Database, Building2, Stethoscope, MonitorCheck,
} from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "@/components/site/Reveal";

const techItems = [
  { icon: FileLock2, text: "Secure electronic medical records" },
  { icon: CloudUpload, text: "Cloud backup" },
  { icon: Zap, text: "Fast document review" },
  { icon: Database, text: "Future-ready centralized database" },
];

const qaItems = ["Medical verification", "Clinic audits", "Compliance monitoring", "Risk assessment", "Continuous improvement"];

const riskItems = [
  "Reduce costly medical repatriations", "Reduce operational disruptions",
  "Improve medical consistency", "Protect crew welfare", "Support regulatory compliance",
];

export default function WhyMedVerify() {
  return (
    <main data-testid="why-page">
      {/* HERO */}
      <section className="pt-40 pb-32 md:pb-36 bg-[#12294d] hero-grain relative overflow-hidden">
        <div className="blob-morph absolute top-16 right-[8%] h-44 w-44 bg-gradient-to-br from-[#2ab5cf]/30 to-[#2ab5cf]/5 hidden lg:block" />
        <div className="floaty-slow absolute bottom-28 right-[28%] h-14 w-14 rounded-full bg-white/10 hidden lg:block" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="overline-label mb-6">
            The MedVerify Difference
          </motion.p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.15] max-w-4xl" data-testid="why-page-heading">
            <MaskedLines
              lines={[
                "Why Choose",
                <>Med<span className="text-[#2ab5cf]">Verify</span>?</>,
              ]}
              startDelay={0.15}
            />
          </h1>
        </div>
        <div className="wave-divider z-10">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,40 C240,80 480,0 720,30 C960,60 1200,10 1440,40 L1440,80 L0,80 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      {/* 01 — INDEPENDENT MEDICAL EXPERTISE: editorial statement */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="blob-morph absolute -top-10 -right-24 h-56 w-56 bg-gradient-to-br from-[#2ab5cf]/12 to-[#1a3a6b]/5 hidden lg:block" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="why-chapter-0">
              <div className="lg:col-span-3">
                <p className="font-display text-8xl text-[#2ab5cf]/25 leading-none">01</p>
                <div className="mt-4 flex items-center gap-3">
                  <UserCheck size={22} className="text-[#1a3a6b]" />
                  <h2 className="font-display text-[#1a3a6b] text-2xl tracking-tight">Independent Medical Expertise</h2>
                </div>
              </div>
              <div className="lg:col-span-9 lg:pl-10 lg:border-l-4 lg:border-[#2ab5cf]">
                <p className="font-display text-[#12294d] text-2xl md:text-4xl tracking-tight leading-snug">
                  Every PEME is reviewed independently by qualified maritime medical doctors.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 02 — TECHNOLOGY DRIVEN: teal icon grid */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="why-chapter-1">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <p className="font-display text-8xl text-[#2ab5cf]/25 leading-none">02</p>
                <div className="mt-4 flex items-center gap-3">
                  <Cpu size={22} className="text-[#1a3a6b]" />
                  <h2 className="font-display text-[#1a3a6b] text-2xl md:text-3xl tracking-tight">Technology Driven</h2>
                </div>
              </div>
            </div>
          </Reveal>
          <Stagger className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techItems.map((t, i) => (
              <StaggerItem key={i}>
                <div className="card-lift bg-white border border-slate-200 rounded-xl p-8 h-full text-center">
                  <span className="mx-auto h-16 w-16 rounded-full bg-[#2ab5cf]/12 flex items-center justify-center">
                    <t.icon size={30} className="text-[#2ab5cf]" />
                  </span>
                  <p className="mt-6 font-bold text-[#12294d]">{t.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* 03 — QUALITY ASSURANCE: navy panel with pill chips */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-10 md:p-16" data-testid="why-chapter-2">
              <div className="relative z-10">
                <p className="font-display text-8xl text-[#2ab5cf]/25 leading-none">03</p>
                <div className="mt-4 flex items-center gap-3">
                  <BadgeCheck size={22} className="text-[#1a3a6b]" />
                  <h2 className="font-display text-[#1a3a6b] text-2xl md:text-3xl tracking-tight">Quality Assurance</h2>
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                  {qaItems.map((q, i) => (
                    <span key={i} className="inline-flex items-center gap-2.5 rounded-full border border-[#2ab5cf]/40 bg-white px-6 py-3 text-[#12294d] font-semibold text-sm md:text-base">
                      <Check size={16} strokeWidth={3} className="text-[#2ab5cf]" />
                      {q}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 04 — RISK REDUCTION: numbered ledger rows */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="why-chapter-3">
          <Reveal className="lg:col-span-4">
            <p className="font-display text-8xl text-[#2ab5cf]/25 leading-none">04</p>
            <div className="mt-4 flex items-center gap-3">
              <TrendingDown size={22} className="text-[#1a3a6b]" />
              <h2 className="font-display text-[#1a3a6b] text-2xl md:text-3xl tracking-tight">Risk Reduction</h2>
            </div>
          </Reveal>
          <Stagger className="lg:col-span-8">
            {riskItems.map((r, i) => (
              <StaggerItem key={i}>
                <div className="flex items-center gap-6 border-b border-slate-200 py-6 group">
                  <span className="font-display text-xl text-[#2ab5cf] w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-[#12294d] font-semibold text-lg md:text-xl">{r}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SYSTEM ACCESS STATEMENT */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="bg-[#12294d] hero-grain relative overflow-hidden rounded-2xl p-10 md:p-14">
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <p className="font-display text-white text-2xl md:text-3xl tracking-tight leading-snug border-l-4 border-[#2ab5cf] pl-8" data-testid="system-access-statement">
                  Client & Clinic both have access to MedVerify system for tracking of candidates fitness status.
                </p>
                <div className="flex items-center justify-center" data-testid="system-access-visual">
                  <div className="flex flex-col items-center gap-3">
                    <span className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-white/[0.06] border border-white/15 flex items-center justify-center">
                      <Building2 size={30} strokeWidth={1.5} className="text-white" />
                    </span>
                    <p className="text-slate-300 text-xs md:text-sm font-semibold tracking-wide">Client</p>
                  </div>
                  <span className="w-10 md:w-16 border-t-2 border-dashed border-[#2ab5cf]/50 mb-7 mx-2" />
                  <div className="flex flex-col items-center gap-3">
                    <span className="h-20 w-20 md:h-24 md:w-24 rounded-2xl bg-[#2ab5cf]/15 border border-[#2ab5cf]/50 flex items-center justify-center">
                      <MonitorCheck size={38} strokeWidth={1.5} className="text-[#2ab5cf]" />
                    </span>
                    <p className="text-white text-xs md:text-sm font-bold tracking-wide">MedVerify System</p>
                  </div>
                  <span className="w-10 md:w-16 border-t-2 border-dashed border-[#2ab5cf]/50 mb-7 mx-2" />
                  <div className="flex flex-col items-center gap-3">
                    <span className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-white/[0.06] border border-white/15 flex items-center justify-center">
                      <Stethoscope size={30} strokeWidth={1.5} className="text-white" />
                    </span>
                    <p className="text-slate-300 text-xs md:text-sm font-semibold tracking-wide">Clinic</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20 md:py-32 bg-[#f8fafc]" data-testid="why-contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <h2 className="font-display font-bold text-[#12294d] text-4xl md:text-5xl tracking-tight" data-testid="why-contact-heading">
              Contact Us
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-md">
              Reach out to discuss centralized PEME review, clinic audits or a tailored compliance program
              for your fleet.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-white border border-slate-200 rounded-xl p-10 space-y-8">
              <div className="flex items-start gap-4">
                <span className="h-11 w-11 rounded-full bg-[#2ab5cf]/12 flex items-center justify-center shrink-0">
                  <Mail size={20} className="text-[#2ab5cf]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Email</p>
                  <a href="mailto:info@medverifys.com" data-testid="contact-email-info" className="mt-2 block text-[#1a3a6b] font-semibold hover:text-[#2ab5cf] transition-colors">info@medverifys.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="h-11 w-11 rounded-full bg-[#2ab5cf]/12 flex items-center justify-center shrink-0">
                  <Phone size={20} className="text-[#2ab5cf]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Mobile</p>
                  <a href="tel:+919867171984" data-testid="contact-phone" className="mt-2 block text-[#1a3a6b] font-semibold hover:text-[#2ab5cf] transition-colors">+91 98671 71984</a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
